import {
  IInventoryAdvancePaymentLine,
  IInventoryAdvancePaymentStatus,
  InventoryAdvancePaymentStatusEnum,
  IInventoryAdvancePaymentSupplierAddress,
  IInventoryAdvancePaymentSupplierContact,
  InventoryAdvancePaymentApprovalState,
  IInventoryAdvancePaymentSupplier,
  IInventoryAdvancePaymentSupplierData,
  IInventoryAdvancePaymentLineData,
  IInventoryAdvancePaymentStatusData,
  IInventoryAdvancePaymentAttachment,
  IInventoryAdvancePaymentAttachmentData,
  IInventoryAdvancePaymentApproval,
  IInventoryAdvancePaymentApprovalData,
  IInventoryAdvancePayment,
  IInventoryAdvancePaymentData,
  IInventoryAdvancePaymentSupplierAddressFieldError,
  IInventoryAdvancePaymentSupplierAddressFieldErrorData,
  IInventoryAdvancePaymentSupplierContactFieldError,
  IInventoryAdvancePaymentSupplierContactFieldErrorData,
  IInventoryAdvancePaymentSupplierFieldError,
  IInventoryAdvancePaymentSupplierFieldErrorData,
  IInventoryAdvancePaymentLineFieldError,
  IInventoryAdvancePaymentLineFieldErrorData,
  IInventoryAdvancePaymentFieldError,
  IInventoryAdvancePaymentFieldErrorData,
  IInventoryAdvancePaymentSupplierAddressData,
  IInventoryAdvancePaymentSupplierContactData,
  InventoryAdvancePaymentPaymentType
} from './inventoryadvancepayment.types';

import {
  IDepartment,
  Department,
  ICurrency,
  Currency,
  GLAccount,
  IGLAccount,
  User,
  IUser,
  Country,
  ICountry,
  AbstractMentionAttachmentComment,
  IAttachment,
  IMentionAttachmentComment,
  IMentionAttachmentCommentData,
  IAttachmentData,
  AbstractAttachment
} from '@/models';
import moment from 'moment';

export class InventoryAdvancePaymentSupplierAddress
  implements IInventoryAdvancePaymentSupplierAddress {
  readonly id?: number;
  readonly country?: ICountry | string;

  readonly address1: string = '';
  readonly address2: string = '';
  readonly city: string = '';
  readonly region: string = '';
  readonly postal_code: string = '';

  constructor(data?: IInventoryAdvancePaymentSupplierAddressData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.address1) {
        this.address1 = data.address1;
      }

      if (data.address2) {
        this.address2 = data.address2;
      }

      if (data.city) {
        this.city = data.city;
      }

      if (data.region) {
        this.region = data.region;
      }

      if (data.country) {
        this.country =
          typeof data.country === 'string'
            ? data.country
            : new Country(data.country);
      }

      if (data.postal_code) {
        this.postal_code = data.postal_code;
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class InventoryAdvancePaymentSupplierContact
  implements IInventoryAdvancePaymentSupplierContact {
  readonly id?: number;
  readonly name: string = '';
  readonly last_name: string = '';
  readonly email: string = '';
  readonly phone: string = '';

  constructor(data?: IInventoryAdvancePaymentSupplierContactData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.name) {
        this.name = data.name;
      }

      if (data.email) {
        this.email = data.email;
      }

      if (data.phone) {
        this.phone = data.phone;
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class InventoryAdvancePaymentSupplier
  implements IInventoryAdvancePaymentSupplier {
  readonly id?: number;
  readonly name: string = '';
  readonly vendor_code: string = '';
  readonly address: IInventoryAdvancePaymentSupplierAddress = new InventoryAdvancePaymentSupplierAddress();
  readonly contact: IInventoryAdvancePaymentSupplierContact = new InventoryAdvancePaymentSupplierContact();

  constructor(data?: IInventoryAdvancePaymentSupplierData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.name) {
        this.name = data.name;
      }

      if (data.vendor_code) {
        this.vendor_code = data.vendor_code;
      }

      if (data.address) {
        this.address = new InventoryAdvancePaymentSupplierAddress(data.address);
      }

      if (data.contact) {
        this.contact = new InventoryAdvancePaymentSupplierContact(data.contact);
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class InventoryAdvancePaymentLine
  implements IInventoryAdvancePaymentLine {
  readonly id?: number;
  readonly item: string = '';
  readonly description: string = '';
  readonly total_price: number = 0;
  readonly account?: IGLAccount | number;
  readonly department?: IDepartment | number;

  constructor(data?: IInventoryAdvancePaymentLineData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.account) {
        this.account =
          typeof data.account === 'number'
            ? data.account
            : new GLAccount(data.account);
      }

      if (data.department) {
        this.department =
          typeof data.department === 'number'
            ? data.department
            : new Department(data.department);
      }

      this.item = data.item;
      this.description = data.description;

      if (data.total_price) {
        this.total_price = data.total_price;
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class InventoryAdvancePaymentStatus
  implements IInventoryAdvancePaymentStatus {
  readonly value: number = InventoryAdvancePaymentStatusEnum.Draft;
  readonly display_name: string = 'Draft';

  constructor(data?: IInventoryAdvancePaymentStatusData) {
    if (data) {
      this.value = data.value;
      this.display_name = data.display_name;
    }
  }

  // Returns color based on state
  getColor(): string {
    switch (this.value) {
      case InventoryAdvancePaymentStatusEnum.Draft:
        return 'draft';

      case InventoryAdvancePaymentStatusEnum.Submitted:
      case InventoryAdvancePaymentStatusEnum.InReview:
      case InventoryAdvancePaymentStatusEnum.Approval2Pending:
      case InventoryAdvancePaymentStatusEnum.InvoiceInProgress:
      case InventoryAdvancePaymentStatusEnum.ClearingInProgress:
        return 'submitted';

      case InventoryAdvancePaymentStatusEnum.Approved:
        return 'approved';

      case InventoryAdvancePaymentStatusEnum.APSpecialistAssigned:
      case InventoryAdvancePaymentStatusEnum.FinanceManagerAssigned:
        return 'blue';

      case InventoryAdvancePaymentStatusEnum.Rejected:
        return 'reject';

      case InventoryAdvancePaymentStatusEnum.InvoiceProcessed:
      case InventoryAdvancePaymentStatusEnum.Cancelled:
        return 'cancel';

      case InventoryAdvancePaymentStatusEnum.Paid:
      case InventoryAdvancePaymentStatusEnum.Cleared:
        return 'done';

      default:
        return 'primary';
    }
  }

  getIcon() {
    switch (this.value) {
      case InventoryAdvancePaymentStatusEnum.Draft:
        return 'mdi-file-document-outline';

      case InventoryAdvancePaymentStatusEnum.Submitted:
        return 'mdi-file-send-outline';

      case InventoryAdvancePaymentStatusEnum.InReview:
        return 'mdi-file-send-outline';

      case InventoryAdvancePaymentStatusEnum.Approval2Pending:
        return 'mdi-file-send-outline';

      case InventoryAdvancePaymentStatusEnum.Approved:
        return 'mdi-file-send-outline';

      case InventoryAdvancePaymentStatusEnum.Rejected:
        return 'mdi-file-send-outline';

      case InventoryAdvancePaymentStatusEnum.Cancelled:
        return 'mdi-alert-circle';

      case InventoryAdvancePaymentStatusEnum.Paid:
        return 'mdi-checkbox-marked-circle-outline';

      case InventoryAdvancePaymentStatusEnum.APSpecialistAssigned:
        return 'mdi-file-document-outline';

      case InventoryAdvancePaymentStatusEnum.FinanceManagerAssigned:
      case InventoryAdvancePaymentStatusEnum.RequestProcessed:
      case InventoryAdvancePaymentStatusEnum.InvoiceInProgress:
      case InventoryAdvancePaymentStatusEnum.InvoiceProcessed:
      case InventoryAdvancePaymentStatusEnum.ClearingInProgress:
      case InventoryAdvancePaymentStatusEnum.Cleared:
      default:
        return 'mdi-file-document-outline';
    }
  }

  static getStateList(): IInventoryAdvancePaymentStatus[] {
    return [
      new InventoryAdvancePaymentStatus({
        value: InventoryAdvancePaymentStatusEnum.Draft,
        display_name: 'Draft'
      }),
      new InventoryAdvancePaymentStatus({
        value: InventoryAdvancePaymentStatusEnum.Submitted,
        display_name: 'Submitted'
      }),
      new InventoryAdvancePaymentStatus({
        value: InventoryAdvancePaymentStatusEnum.InReview,
        display_name: 'Approval I Pending'
      }),
      new InventoryAdvancePaymentStatus({
        value: InventoryAdvancePaymentStatusEnum.Approval2Pending,
        display_name: 'Approval II Pending'
      }),
      new InventoryAdvancePaymentStatus({
        value: InventoryAdvancePaymentStatusEnum.Approved,
        display_name: 'Approved'
      }),
      new InventoryAdvancePaymentStatus({
        value: InventoryAdvancePaymentStatusEnum.APSpecialistAssigned,
        display_name: 'A/P Specialist Assigned'
      }),
      new InventoryAdvancePaymentStatus({
        value: InventoryAdvancePaymentStatusEnum.Rejected,
        display_name: 'Rejected'
      }),
      new InventoryAdvancePaymentStatus({
        value: InventoryAdvancePaymentStatusEnum.Cancelled,
        display_name: 'Cancelled'
      }),
      new InventoryAdvancePaymentStatus({
        value: InventoryAdvancePaymentStatusEnum.Paid,
        display_name: 'Paid'
      }),
      new InventoryAdvancePaymentStatus({
        value: InventoryAdvancePaymentStatusEnum.FinanceManagerAssigned,
        display_name: 'Finance Manager Assigned'
      }),
      new InventoryAdvancePaymentStatus({
        value: InventoryAdvancePaymentStatusEnum.RequestProcessed,
        display_name: 'Request Processed'
      }),
      new InventoryAdvancePaymentStatus({
        value: InventoryAdvancePaymentStatusEnum.InvoiceInProgress,
        display_name: 'Invoice In Progress'
      }),
      new InventoryAdvancePaymentStatus({
        value: InventoryAdvancePaymentStatusEnum.InvoiceProcessed,
        display_name: 'Invoice Processed'
      }),
      new InventoryAdvancePaymentStatus({
        value: InventoryAdvancePaymentStatusEnum.ClearingInProgress,
        display_name: 'Clearing In Progress'
      }),
      new InventoryAdvancePaymentStatus({
        value: InventoryAdvancePaymentStatusEnum.Cleared,
        display_name: 'Cleared'
      })
    ];
  }

  static getStatusFromState(state: InventoryAdvancePaymentStatusEnum) {
    return InventoryAdvancePaymentStatus.getStateList().find(
      status => state === status.value
    );
  }

  static getStatusMessageFromState(state: InventoryAdvancePaymentStatusEnum) {
    return (
      InventoryAdvancePaymentStatus.getStatusFromState(state)?.display_name ??
      ' - '
    );
  }
}

export class InventoryAdvancePaymentAttachment
  implements IInventoryAdvancePaymentAttachment {
  readonly id?: number | undefined;
  readonly file: string | File;
  readonly filename: string = '';

  constructor(data: IInventoryAdvancePaymentAttachmentData) {
    if (data.id) {
      this.id = data.id;
    }

    if (data.filename) {
      this.filename = data.filename;
    }

    this.file = data.file;
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class InventoryAdvancePaymentApproval
  implements IInventoryAdvancePaymentApproval {
  readonly id?: number;
  readonly approver: IUser;
  readonly status: InventoryAdvancePaymentApprovalState;
  readonly created_at?: Date | string;
  readonly updated_at?: Date | string;

  constructor(data: IInventoryAdvancePaymentApprovalData) {
    if (data.id) {
      this.id = data.id;
    }
    this.approver = new User(data.approver);

    this.status = data.status ?? InventoryAdvancePaymentApprovalState.TENTATIVE;

    if (data.created_at) {
      this.created_at = data.created_at;
    }

    if (data.updated_at) {
      this.updated_at = data.updated_at;
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class InventoryAdvancePaymentCommentAttachment extends AbstractAttachment {
  constructor(data: IAttachmentData) {
    super(data);
  }

  toString(): string {
    return this.filename;
  }
}
export class InventoryAdvancePaymentComment
  extends AbstractMentionAttachmentComment
  implements IMentionAttachmentComment {
  readonly text: string = '';
  readonly attachments: IAttachment[] = [];

  constructor(data?: IMentionAttachmentCommentData) {
    super(data);

    if (data) {
      this.text = data.text ?? '';

      if (data.attachments) {
        this.attachments = data.attachments.map(
          attachment => new InventoryAdvancePaymentCommentAttachment(attachment)
        );
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  toString() {
    return this.text;
  }
}

export class InventoryAdvancePayment implements IInventoryAdvancePayment {
  readonly id?: number;
  readonly amount: number = 0;
  readonly num_items: number = 0;
  readonly payment?: InventoryAdvancePaymentPaymentType;
  readonly po_number?: string;
  readonly reason?: string;
  readonly note?: string;
  readonly is_urgent?: boolean;
  readonly is_provide_payment_detail?: boolean;
  readonly status: IInventoryAdvancePaymentStatus = new InventoryAdvancePaymentStatus();
  readonly currency?: ICurrency | number;
  readonly attachments: IInventoryAdvancePaymentAttachment[] = [];
  readonly lines: IInventoryAdvancePaymentLine[] = [];
  readonly supplier: IInventoryAdvancePaymentSupplier = new InventoryAdvancePaymentSupplier();
  readonly required_date?: Date | string;
  readonly created_at?: Date | string;
  readonly updated_at?: Date | string;

  readonly approvers: InventoryAdvancePaymentApproval[] = [];
  readonly ap_specialist?: IUser | number;
  readonly finance_manager?: IUser | number | null = null;
  readonly owner?: IUser | number;

  constructor(data?: IInventoryAdvancePaymentData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      // Need to check for undefined so that a value of zero would be treated
      // as a valid number
      if (typeof data.amount === 'number') {
        this.amount = data.amount;
      }

      if (typeof data.num_items == 'number') {
        this.num_items = data.num_items;
      }

      if (typeof data.payment == 'number') {
        this.payment = data.payment;
      }

      if (data.po_number) {
        this.po_number = data.po_number;
      }

      if (data.owner) {
        this.owner =
          typeof data.owner === 'number' ? data.owner : new User(data.owner);
      }

      if (data.reason) {
        this.reason = data.reason;
      }

      if (data.note) {
        this.note = data.note;
      }

      if (data.approvers) {
        this.approvers = data.approvers.map(
          approver => new InventoryAdvancePaymentApproval(approver)
        );
      }

      if (data.ap_specialist) {
        this.ap_specialist =
          typeof data.ap_specialist === 'number'
            ? data.ap_specialist
            : new User(data.ap_specialist);
      }

      if (data.finance_manager) {
        this.finance_manager =
          typeof data.finance_manager === 'number'
            ? data.finance_manager
            : new User(data.finance_manager);
      }

      if (data.status) {
        this.status = new InventoryAdvancePaymentStatus(data.status);
      }

      if (data.currency) {
        this.currency =
          typeof data.currency === 'number'
            ? data.currency
            : new Currency(data.currency);
      }

      if (data.lines) {
        this.lines = data.lines.map(
          line => new InventoryAdvancePaymentLine(line)
        );
      }

      if (data.attachments) {
        this.attachments = data.attachments.map(
          attachment => new InventoryAdvancePaymentAttachment(attachment)
        );
      }

      if (data.supplier) {
        this.supplier = new InventoryAdvancePaymentSupplier(data.supplier);
      }

      this.is_urgent = data.is_urgent ?? false;
      this.is_provide_payment_detail = data.is_provide_payment_detail ?? false;

      if (data.required_date) {
        this.required_date = data.required_date;
      }

      if (data.created_at) {
        this.created_at = data.created_at;
      }

      if (data.updated_at) {
        this.updated_at = data.updated_at;
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  addLine(line: IInventoryAdvancePaymentLine): void {
    this.lines.push(line);
  }

  removeLine(index: number): void {
    this.lines.splice(index, 1);
  }

  attachFile(file: File): void {
    this.attachments.push(
      new InventoryAdvancePaymentAttachment({ file: file })
    );
  }

  detachFile(file: string | File): void {
    const index = this.attachments?.findIndex(
      attachment => attachment.file == file
    );

    if (index > -1) {
      this.attachments?.splice(index, 1);
    }
  }

  // ## Transition Permissions and Conditions ##
  /**
   * If user can approve payment and transition from
   * from - [InventoryAdvancePaymentStatusEnum.Submitted, InventoryAdvancePaymentStatusEnum.InReview];
   * to - [InventoryAdvancePaymentStatusEnum.Approved]
   */
  isUserApprover(userId: number): boolean {
    return this.approvers.some(
      approver => userId === (approver.approver as IUser).id
    );
  }

  /**
   * If user is the finance manager; since only the assigned finance manager can start the process
   * from - InventoryAdvancePaymentStatusEnum.Approved
   * to - InventoryAdvancePaymentStatusEnum.FinanceManagerAssigned
   */
  isUserFinanceManager(userId: number): boolean {
    return Boolean(
      this.finance_manager && (this.finance_manager as IUser).id === userId
    );
  }

  /**
   * If user is the AP specialist; since only the assigned ap speclist can confirm payment
   */
  isUserApSpecialist(userId: number): boolean {
    return Boolean(
      this.ap_specialist && (this.ap_specialist as IUser).id === userId
    );
  }
}

export class InventoryAdvancePaymentSupplierAddressFieldError
  implements IInventoryAdvancePaymentSupplierAddressFieldError {
  readonly address1: string[] = [];
  readonly address2: string[] = [];
  readonly city: string[] = [];
  readonly region: string[] = [];
  readonly country: string[] = [];
  readonly postal_code: string[] = [];

  constructor(data?: IInventoryAdvancePaymentSupplierAddressFieldErrorData) {
    if (data) {
      if (data.address1) {
        this.address1 = data.address1;
      }

      if (data.address2) {
        this.address2 = data.address2;
      }

      if (data.city) {
        this.city = data.city;
      }

      if (data.region) {
        this.region = data.region;
      }

      if (data.country) {
        this.country = data.country;
      }

      if (data.postal_code) {
        this.postal_code = data.postal_code;
      }
    }
  }

  clearErrors(): void {
    this.address1.splice(0, this.address1.length);
    this.address2.splice(0, this.address2.length);
    this.city.splice(0, this.city.length);
    this.region.splice(0, this.region.length);
    this.country.splice(0, this.country.length);
    this.postal_code.splice(0, this.postal_code.length);
  }
}

export class InventoryAdvancePaymentSupplierContactFieldError
  implements IInventoryAdvancePaymentSupplierContactFieldError {
  readonly name: string[] = [];
  readonly email: string[] = [];
  readonly phone: string[] = [];

  constructor(data?: IInventoryAdvancePaymentSupplierContactFieldErrorData) {
    if (data) {
      if (data.name) {
        this.name = data.name;
      }

      if (data.email) {
        this.email = data.email;
      }

      if (data.phone) {
        this.phone = data.phone;
      }
    }
  }

  clearErrors(): void {
    this.name.splice(0, this.name.length);
    this.email.splice(0, this.email.length);
    this.phone.splice(0, this.phone.length);
  }
}

export class InventoryAdvancePaymentSupplierFieldError
  implements IInventoryAdvancePaymentSupplierFieldError {
  readonly name: string[] = [];
  readonly vendor_code: string[] = [];
  readonly address: IInventoryAdvancePaymentSupplierAddressFieldError = new InventoryAdvancePaymentSupplierAddressFieldError();
  readonly contact: IInventoryAdvancePaymentSupplierContactFieldError = new InventoryAdvancePaymentSupplierContactFieldError();

  constructor(data?: IInventoryAdvancePaymentSupplierFieldErrorData) {
    if (data) {
      if (data.name) {
        this.name = data.name;
      }

      if (data.vendor_code) {
        this.vendor_code = data.vendor_code;
      }

      if (data.address) {
        this.address = new InventoryAdvancePaymentSupplierAddressFieldError(
          data.address
        );
      }

      if (data.contact) {
        if (Array.isArray(data.contact)) {
          // incase the servers returns the contat as an array only
          // HACK: we enforce what the display shows is jkust the fields inside are all errors
          // all mandatory
          this.contact = new InventoryAdvancePaymentSupplierContactFieldError({
            name: data.contact
          });
        } else {
          this.contact = new InventoryAdvancePaymentSupplierContactFieldError(
            data.contact
          );
        }
      }
    }
  }

  clearErrors(): void {
    this.name.splice(0, this.name.length);
    this.vendor_code.splice(0, this.vendor_code.length);
    this.address.clearErrors();
    this.contact.clearErrors();
  }
}

export class InventoryAdvancePaymentLineFieldError
  implements IInventoryAdvancePaymentLineFieldError {
  readonly item: string[] = [];
  readonly description: string[] = [];
  readonly quantity: string[] = [];
  readonly unit_price: string[] = [];
  readonly account: string[] = [];
  readonly department: string[] = [];

  constructor(data?: IInventoryAdvancePaymentLineFieldErrorData) {
    if (data) {
      if (data.item) {
        this.item = data.item;
      }

      if (data.description) {
        this.description = data.description;
      }

      if (data.quantity) {
        this.quantity = data.quantity;
      }

      if (data.unit_price) {
        this.unit_price = data.unit_price;
      }

      if (data.account) {
        this.account = data.account;
      }

      if (data.department) {
        this.department = data.department;
      }
    }
  }

  clearErrors(): void {
    this.item.splice(0, this.item.length);
    this.description.splice(0, this.description.length);
    this.quantity.splice(0, this.quantity.length);
    this.unit_price.splice(0, this.unit_price.length);
    this.account.splice(0, this.account.length);
    this.department.splice(0, this.department.length);
  }

  static isInventoryAdvancePaymentLineFieldError(
    data: IInventoryAdvancePaymentLineFieldErrorData
  ): boolean {
    if (data) {
      if (data.item && Array.isArray(data.item)) {
        return true;
      } else if (data.description && Array.isArray(data.description)) {
        return true;
      } else if (data.quantity && Array.isArray(data.quantity)) {
        return true;
      } else if (data.unit_price && Array.isArray(data.unit_price)) {
        return true;
      } else if (data.account && Array.isArray(data.account)) {
        return true;
      } else if (data.department && Array.isArray(data.department)) {
        return true;
      }
    }

    return false;
  }
}

export class InventoryAdvancePaymentFieldError
  implements IInventoryAdvancePaymentFieldError {
  readonly currency: string[] = [];
  readonly reason: string[] = [];
  readonly project: string[] = [];
  readonly payment: string[] = [];
  readonly required_date: string[] = [];
  readonly po_number: string[] = [];
  readonly is_urgent: string[] = [];
  readonly attachments: string[] = [];
  readonly note: string[] = [];
  readonly supplier: IInventoryAdvancePaymentSupplierFieldError = new InventoryAdvancePaymentSupplierFieldError();
  readonly lines: IInventoryAdvancePaymentLineFieldError[] = [];

  constructor(data?: IInventoryAdvancePaymentFieldErrorData) {
    if (data) {
      if (data.currency) {
        this.currency = data.currency;
      }

      if (data.reason) {
        this.reason = data.reason;
      }

      if (data.required_date) {
        this.required_date = data.required_date;
      }

      if (data.po_number) {
        this.po_number = data.po_number;
      }

      if (data.is_urgent) {
        this.is_urgent = data.is_urgent;
      }

      if (data.attachments) {
        this.attachments = data.attachments;
      }

      if (data.note) {
        this.note = data.note;
      }

      if (data.payment) {
        this.payment = data.payment;
      }

      if (data.supplier) {
        if (Array.isArray(data.supplier)) {
          // incase the servers returns the contat as an array only
          // HACK: we enforce what the display shows is jkust the fields inside are all errors
          // all mandatory
          this.supplier = new InventoryAdvancePaymentSupplierFieldError({
            name: data.supplier,
            contact: new InventoryAdvancePaymentSupplierContactFieldError({
              name: data.supplier
            })
          });
        } else {
          this.supplier = new InventoryAdvancePaymentSupplierFieldError(
            data.supplier
          );
        }
      }

      if (data.lines) {
        this.lines = data.lines.map(
          line => new InventoryAdvancePaymentLineFieldError(line)
        );
      }
    }
  }

  clearErrors(): void {
    this.currency.splice(0, this.currency.length);
    this.project.splice(0, this.project.length);
    this.reason.splice(0, this.reason.length);
    this.required_date.splice(0, this.required_date.length);
    this.po_number.splice(0, this.po_number.length);
    this.is_urgent.splice(0, this.is_urgent.length);
    this.note.splice(0, this.note.length);
    this.attachments.splice(0, this.attachments.length);
    this.payment.splice(0, this.payment.length);
    this.lines.splice(0, this.lines.length);
    this.supplier.clearErrors();
  }

  static isInventoryAdvancePaymentFieldError(
    data: IInventoryAdvancePaymentFieldErrorData
  ): boolean {
    if (data.currency && Array.isArray(data.currency)) {
      return true;
    } else if (data.reason && Array.isArray(data.reason)) {
      return true;
    } else if (data.required_date && Array.isArray(data.required_date)) {
      return true;
    } else if (data.po_number && Array.isArray(data.po_number)) {
      return true;
    } else if (data.is_urgent && Array.isArray(data.is_urgent)) {
      return true;
    } else if (data.note && Array.isArray(data.note)) {
      return true;
    } else if (data.attachments && Array.isArray(data.attachments)) {
      return true;
    } else if (data.payment && Array.isArray(data.payment)) {
      return true;
    } else if (data.lines && Array.isArray(data.lines)) {
      return true;
    } else if (data.supplier) {
      return true;
    }

    return false;
  }
}
